<template>
  <div class="flex flex-col items-center text-center gap-3">
    <img
      v-if="profile.avatar_url"
      :src="resolveMediaUrl(profile.avatar_url) || undefined"
      class="w-16 h-16 rounded-full object-cover shadow-ink-sm"
      alt=""
    />
    <div v-else class="w-16 h-16 rounded-full bg-[hsl(var(--primary)/0.12)] text-[hsl(var(--primary))] flex items-center justify-center text-xl font-semibold">
      {{ (profile.display_name || profile.username).charAt(0).toUpperCase() }}
    </div>

    <div>
      <h3 class="text-[16px] font-semibold">{{ profile.display_name || "@" + profile.username }}</h3>
      <p v-if="profile.display_name" class="text-[12px] text-ink2">@{{ profile.username }}</p>
    </div>

    <p class="text-[15px] font-semibold">{{ $t("conflict.title") }}</p>
    <p class="text-[12px] text-ink2 leading-snug">{{ $t("conflict.subtitle") }}</p>

    <p v-if="error" class="w-full flex items-start gap-2 px-3 py-2 rounded-md bg-[hsl(var(--danger)/0.08)] text-[hsl(var(--danger))] text-[12px] text-left">
      <CircleAlert class="w-3.5 h-3.5 mt-0.5 shrink-0" />
      {{ error }}
    </p>

    <button
      v-if="profile.login_methods.includes('google')"
      type="button"
      :disabled="loading"
      @click="loginWith('google')"
      class="w-full h-10 rounded-md border border-border text-[13px] font-semibold hover:bg-[hsl(var(--muted))] transition-colors disabled:opacity-60"
    >
      {{ $t("login.google") }}
    </button>
    <button
      v-if="profile.login_methods.includes('telegram')"
      type="button"
      :disabled="loading"
      @click="loginWith('telegram')"
      class="w-full h-10 rounded-md border border-border text-[13px] font-semibold hover:bg-[hsl(var(--muted))] transition-colors disabled:opacity-60"
    >
      {{ $t("login.telegramOidc") }}
    </button>

    <form v-if="hasPasswordLogin" @submit.prevent="loginWithPassword" class="w-full space-y-2">
      <input
        v-model="identifier"
        type="text"
        required
        autocomplete="username"
        :placeholder="$t('login.email')"
        class="w-full h-10 px-3 rounded-md border border-border bg-[hsl(var(--muted)/0.4)] text-[13px] outline-none focus:ring-2 focus:ring-[hsl(var(--primary)/0.3)]"
      />
      <input
        v-model="password"
        type="password"
        required
        autocomplete="current-password"
        :placeholder="$t('login.passwordPlaceholder')"
        class="w-full h-10 px-3 rounded-md border border-border bg-[hsl(var(--muted)/0.4)] text-[13px] outline-none focus:ring-2 focus:ring-[hsl(var(--primary)/0.3)]"
      />
      <button
        type="submit"
        :disabled="loading || !identifier || !password"
        class="w-full h-10 rounded-md bg-[hsl(var(--primary))] text-white text-[13px] font-semibold hover:bg-[hsl(158,38%,30%)] transition-colors disabled:opacity-60 flex items-center justify-center gap-1.5"
      >
        <Loader2 v-if="loading" class="w-4 h-4 animate-spin" />
        {{ $t("conflict.loginWithPassword") }}
      </button>
    </form>

    <button type="button" @click="$emit('cancel')" class="text-[12px] text-ink2 hover:text-[hsl(var(--foreground))] transition-colors">
      {{ $t("conflict.cancel") }}
    </button>
  </div>
</template>

<script setup lang="ts">
import { computed, ref } from "vue";
import { useI18n } from "vue-i18n";
import { CircleAlert, Loader2 } from "lucide-vue-next";
import api, { resolveMediaUrl } from "../api";
import { useAuthStore } from "../store/auth";
import {
  PENDING_CLAIM_LINK_TOKEN_KEY,
  startOAuthLogin,
  type OAuthProvider,
} from "../composables/useOAuthLogin";

const { t } = useI18n();

const props = defineProps<{
  profile: {
    username: string;
    display_name: string | null;
    avatar_url: string | null;
    login_methods: string[];
  };
  // null для коллизии по email при регистрации (см. service.register_with_email -
  // довязывать нечего, эта форма тогда просто логинит в существующий аккаунт).
  linkToken: string | null;
}>();

const emit = defineEmits<{ (e: "resolved"): void; (e: "cancel"): void }>();

const auth = useAuthStore();
const identifier = ref("");
const password = ref("");
const loading = ref(false);
const error = ref("");

// Пароль общий для входа и по email, и по телефону (форма входа объединена -
// см. LoginModal.vue) - показываем эту форму, если у конфликтующего профиля
// есть хоть один из этих способов, а не только email.
const hasPasswordLogin = computed(
  () => props.profile.login_methods.includes("email") || props.profile.login_methods.includes("phone")
);

// Redirect-провайдеры (Google/Telegram) не могут довязать credential сразу
// в этом компоненте - вход уводит на чужой домен и обратно. link_token
// переживает этот переход в sessionStorage; OAuthCallbackView.vue
// подхватывает и вызывает /auth/identities/claim сам, см. его код.
const loginWith = async (provider: OAuthProvider) => {
  loading.value = true;
  error.value = "";
  try {
    if (props.linkToken) {
      sessionStorage.setItem(PENDING_CLAIM_LINK_TOKEN_KEY, props.linkToken);
    }
    await startOAuthLogin(provider, "login");
  } catch {
    sessionStorage.removeItem(PENDING_CLAIM_LINK_TOKEN_KEY);
    error.value = t("login.oauthGenericError");
    loading.value = false;
  }
};

const loginWithPassword = async () => {
  loading.value = true;
  error.value = "";
  try {
    const form = new URLSearchParams();
    form.append("username", identifier.value);
    form.append("password", password.value);
    form.append("grant_type", "password");
    form.append("scope", "");
    const loginRes = await api.post("/auth/login", form, {
      headers: { "Content-Type": "application/x-www-form-urlencoded" },
    });
    auth.setTokens(loginRes.data.access_token, loginRes.data.refresh_token);
    if (props.linkToken) {
      await api.post("/auth/identities/claim", { link_token: props.linkToken });
    }
    const me = await api.get("/auth/me");
    auth.setProfile(me.data);
    emit("resolved");
  } catch (err: any) {
    error.value =
      err?.response?.status === 401
        ? t("login.errorInvalid")
        : t("conflict.error");
  } finally {
    loading.value = false;
  }
};
</script>
